import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Target, Globe, Ticket, Radio, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';

const AUDIENCE_TAGS = [
  { id: 'families', label: 'Familles' },
  { id: 'young', label: 'Jeunes 18-25' },
  { id: 'adults', label: 'Adultes 25-45' },
  { id: 'seniors', label: '45+' },
  { id: 'csp_plus', label: 'CSP+' },
  { id: 'locals', label: 'Locaux' },
  { id: 'tourists', label: 'Touristes' },
  { id: 'students', label: 'Étudiants' },
  { id: 'music_lovers', label: 'Mélomanes' },
  { id: 'foodies', label: 'Foodies' },
  { id: 'eco', label: 'Éco-conscients' },
  { id: 'art', label: 'Amateurs d\'art' },
];

const MEDIA_TAGS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'press', label: 'Presse locale' },
  { id: 'radio', label: 'Radio' },
  { id: 'affichage', label: 'Affichage urbain' },
  { id: 'influencers', label: 'Influenceurs' },
  { id: 'newsletter', label: 'Newsletter' },
];

export default function CreateFestivalPotentialPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const [dailyPax, setDailyPax] = useState('');
  const [totalCapacity, setTotalCapacity] = useState('');
  const [audienceTags, setAudienceTags] = useState<string[]>([]);
  const [audienceProfile, setAudienceProfile] = useState('');
  const [catchmentArea, setCatchmentArea] = useState('');
  const [ticketingUrl, setTicketingUrl] = useState('');
  const [mediaTags, setMediaTags] = useState<string[]>([]);
  const [mediaDetails, setMediaDetails] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const snap = await getDoc(doc(db, 'festivals', activeId));
        if (snap.exists()) {
          const d = snap.data();
          setDailyPax(d.dailyPax?.toString() ?? '');
          setTotalCapacity(d.capacity?.toString() ?? '');
          setAudienceTags(d.audienceTags ?? []);
          setAudienceProfile(d.audienceProfile ?? '');
          setCatchmentArea(d.catchmentArea ?? '');
          setTicketingUrl(d.ticketingUrl ?? '');
          setMediaTags(d.mediaTags ?? []);
          setMediaDetails(d.mediaDetails ?? '');
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [user]);

  const toggleTag = (list: string[], setList: (v: string[]) => void, id: string) => {
    setList(list.includes(id) ? list.filter(t => t !== id) : [...list, id]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        dailyPax: parseInt(dailyPax) || null,
        capacity: parseInt(totalCapacity) || null,
        audienceTags,
        audienceProfile,
        catchmentArea,
        ticketingUrl,
        mediaTags,
        mediaDetails,
      }, { merge: true });
      navigate('/create-festival/step-3');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans relative overflow-x-hidden pb-32">
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      <div className="max-w-2xl mx-auto px-5 pt-12 pb-8">
        {/* Title */}
        <div className="mb-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#FF5C00] mb-3">Étape 4 / 7</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-4">
            Potentiel & attractivité
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed">
            Ces infos convainquent les exposants de postuler. Un festival bien renseigné reçoit 3× plus de candidatures.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">

          {/* Fréquentation */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-[#FF5C00]" />
              </div>
              <div>
                <p className="font-bold text-white">Fréquentation attendue</p>
                <p className="text-sm text-gray-500">Estimations pour cette édition</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Visiteurs / jour</label>
                <input
                  type="number"
                  value={dailyPax}
                  onChange={e => setDailyPax(e.target.value)}
                  placeholder="Ex : 5 000"
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Total festival</label>
                <input
                  type="number"
                  value={totalCapacity}
                  onChange={e => setTotalCapacity(e.target.value)}
                  placeholder="Ex : 15 000"
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Profil du public */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                <Target className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="font-bold text-white">Profil du public</p>
                <p className="text-sm text-gray-500">Sélectionnez les profils qui correspondent</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              {AUDIENCE_TAGS.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(audienceTags, setAudienceTags, tag.id)}
                  className={`px-3 py-1.5 rounded-xl text-sm font-bold transition-all border ${
                    audienceTags.includes(tag.id)
                      ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
            <textarea
              value={audienceProfile}
              onChange={e => setAudienceProfile(e.target.value)}
              placeholder="Décrivez votre public en quelques mots... (optionnel)"
              rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors resize-none text-sm"
            />
          </div>

          {/* Zone de chalandise + billetterie */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-bold text-white">Rayonnement</p>
                <p className="text-sm text-gray-500">Zone d'attraction et billetterie</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Zone de chalandise</label>
                <input
                  type="text"
                  value={catchmentArea}
                  onChange={e => setCatchmentArea(e.target.value)}
                  placeholder="Ex : Région PACA, rayon 100 km, national..."
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2 flex items-center gap-2">
                  <Ticket className="w-3.5 h-3.5" /> Lien billetterie
                </label>
                <input
                  type="url"
                  value={ticketingUrl}
                  onChange={e => setTicketingUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Communication & médias */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Radio className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-bold text-white">Communication prévue
                  <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-blue-400 bg-blue-400/10 border border-blue-400/20 px-1.5 py-0.5 rounded-full align-middle">+Score</span>
                </p>
                <p className="text-sm text-gray-500">Quels canaux allez-vous activer ?</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              {MEDIA_TAGS.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(mediaTags, setMediaTags, tag.id)}
                  className={`px-3 py-1.5 rounded-xl text-sm font-bold transition-all border ${
                    mediaTags.includes(tag.id)
                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
            <textarea
              value={mediaDetails}
              onChange={e => setMediaDetails(e.target.value)}
              placeholder="Ex : Partenariat France Bleu, 50K abonnés Instagram, affichage 4x3 en centre-ville..."
              rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors resize-none text-sm"
            />
          </div>

          {/* Bottom nav */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-8 mt-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm"
              >
                Précédent
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="w-full md:flex-1 bg-white text-black font-black py-4 px-8 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
              >
                {isSaving ? 'Enregistrement...' : 'Continuer'}
                {!isSaving && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-600 text-center mt-4">Toutes les informations peuvent être modifiées après publication.</p>
          </div>

        </form>
      </div>
    </div>
  );
}
